import React, { useState } from 'react';
import useFlipAnimation from '../hooks/useFlipAnimation';
import useEntrySettled from '../hooks/useEntrySettled';
import { EyeOff, ChevronDown, ChevronUp, Trash2, SearchX } from 'lucide-react';
import ItemCard, { FlagMark } from './ItemCard';
import InputField from './InputField';
import { ui } from '../ui';

// Per-tab wording, labs are "ordered", history/exam findings are "elicited".
const TAB_COPY = {
  history: {
    heading: 'History: what do you want to ask?',
    missed: 'Not elicited',
    unmatched: 'Negative / not a feature of this case',
    keyPlaceholder: ["Finding (e.g. 'Orthopnea')", "Patient's answer"],
  },
  physical: {
    heading: 'Physical: what do you want to examine?',
    missed: 'Not elicited',
    unmatched: 'Negative / not a feature of this case',
    keyPlaceholder: ["Finding (e.g. 'JVP')", 'Exam finding / value'],
  },
  labs: {
    heading: 'Labs: what do you want to order?',
    missed: 'Not ordered',
    unmatched: 'Normal / negative: not contributory',
    keyPlaceholder: ["Test (e.g. 'Troponin')", 'Result'],
  },
};

// Facilitator-only answer key for the current tab. Collapsed by default:
// facilitators often project their screen; don't expand while projecting.
function CaseKeyPanel({ tab, findings, columns, withFlag, placeholders, onAddFinding, onRemoveFinding }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [detail, setDetail] = useState('');
  const [category, setCategory] = useState(columns[0].id);
  const [flag, setFlag] = useState('abn');

  const add = () => {
    if (!text.trim()) return;
    onAddFinding(tab, { text, detail, category, ...(withFlag ? { flag } : {}) });
    setText('');
    setDetail('');
  };

  return (
    <div className="mt-section border border-dashed border-accent/40 rounded-card bg-accent-tint/40">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-meta font-medium text-accent"
        aria-expanded={open}
      >
        <span className="flex items-center gap-1.5">
          <EyeOff className="w-3.5 h-3.5" />
          Answer key ({findings.length}), visible only to you; keep closed while projecting
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-1.5">
          {findings.map((f) => (
            <div key={f.id} className={`${ui.panel} flex items-start justify-between gap-2 px-3 py-1.5`}>
              <span className="text-meta text-ink">
                <span className="font-medium">{f.text}</span>
                {f.detail && <span className="text-ink-2">, {f.detail}</span>}
                {withFlag && f.flag && <FlagMark flag={f.flag} className="ml-2" />}
                <span className="ml-2 text-meta text-ink-2 uppercase tracking-wide">
                  {columns.find((c) => c.id === f.category)?.label || columns[0].label}
                </span>
              </span>
              <button onClick={() => onRemoveFinding(tab, f.id)} className={`${ui.iconBtn} w-7 h-7 hover:text-flag-red flex-shrink-0`} aria-label={`Remove finding "${f.text}"`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <div className="flex flex-col sm:flex-row gap-1.5 pt-1">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholders[0]} className={`${ui.input} flex-1`} />
            <input value={detail} onChange={(e) => setDetail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder={placeholders[1]} className={`${ui.input} flex-1`} />
            {withFlag && (
              <select value={flag} onChange={(e) => setFlag(e.target.value)} className={ui.select} aria-label="Result flag">
                <option value="high">High</option>
                <option value="low">Low</option>
                <option value="abn">Abnormal</option>
                <option value="ok">Normal</option>
                <option value="pending">Pending</option>
              </select>
            )}
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={ui.select} aria-label="Finding section">
              {columns.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <button onClick={add} disabled={!text.trim()} className={ui.btnOutline}>
              Add finding
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// A case finding the class never elicited/ordered, shown inline in its own
// section after the reveal so the gap is visible in context: a dashed
// blue-grey border, the "pending" colour, and no fill.
function MissedFindingCard({ finding, label }) {
  const settled = useEntrySettled();
  return (
    <div
      className={`item-enter ${settled} card bg-surface border-dashed border-flag-pending rounded-card p-3 mb-2`}
      data-state="missed"
      title={label}
    >
      <p className="flex items-start gap-1.5 text-meta font-medium text-ink-2">
        <SearchX className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-flag-pending" aria-label={label} />
        <span>
          {finding.text}
          {finding.flag && <FlagMark flag={finding.flag} className="ml-2" />}
        </span>
      </p>
      {finding.detail && (
        <p className="text-body font-semibold text-ink mt-0.5 ml-5 leading-snug">
          {finding.detail}
        </p>
      )}
    </div>
  );
}

/**
 * The History / Physical / Labs brainstorm board: categorized columns of
 * student entries. After the facilitator reveals, entries matching the case's
 * real findings light up (with the patient's actual answer), the rest grey
 * out, and findings nobody elicited appear inline in their own sections.
 */
export default function BrainstormBoard({
  tab, board, columns, rowId,
  userName, identityKey, isFacilitator, revealed = true, readOnly = false,
  caseActive = false, findings = [],
  addItem, editItem, deleteItem, vote,
  setMatchOverride, onAddFinding, onRemoveFinding,
}) {
  const copy = TAB_COPY[tab] || TAB_COPY.history;
  const isLabs = tab === 'labs';
  // Cards glide when a vote re-ranks them within their category (or when the
  // flowing layout carries them into the next column).
  const setFlipRef = useFlipAnimation();

  const canModify = (item) =>
    isFacilitator || (item.addedByKey ? item.addedByKey === identityKey : item.addedBy === userName);

  // `section` is resolved in deriveSession (unknown/absent categories fall
  // back to the first column), so no finding can go missing from the board.
  const missedFor = (colId) =>
    board.showMissed ? board.missedFindings.filter((f) => f.section === colId) : [];

  return (
    <div>
      {/* A plain heading row: the step's question, and its status as a chip. */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className={ui.heading}>{copy.heading}</h2>
        {caseActive && board.revealed && (
          <span className={ui.chip}>{isLabs ? 'results revealed' : 'matches revealed'}</span>
        )}
      </div>
      {/* Category groups flow through balanced columns (see .board-flow in
          index.css): the grouping and order are kept, the holes are not.
          Each group stays one [data-column] block: label, cards, add field. */}
      <div className="board-flow">
        {columns.map((col) => (
          <div key={col.id} className="mb-4" data-column={col.id}>
            <h3 className={`${ui.label} mb-2`}>{col.label}</h3>
            {board.columns[col.id].map((item) => (
              <div key={item.id} className="flip-item" ref={setFlipRef(item.id)}>
                <ItemCard
                  item={item}
                  /* A misfiled entry is displayed here but still STORED under
                     item.sourceColumn, writes must follow the storage path. */
                  onVote={(v) => vote(rowId, item.sourceColumn || col.id, item.id, v)}
                  onDelete={() => deleteItem(rowId, item.sourceColumn || col.id, item.id, item.private)}
                  onEdit={(text) => editItem(rowId, item.sourceColumn || col.id, item.id, text, item.private)}
                  canModify={canModify(item)}
                  isSmall
                  revealed={revealed}
                  readOnly={readOnly}
                  unmatchedLabel={copy.unmatched}
                  onOverride={
                    isFacilitator && board.revealed && !readOnly
                      ? (val) => setMatchOverride(item.id, val)
                      : null
                  }
                />
              </div>
            ))}
            {missedFor(col.id).map((f) => (
              <div key={f.id} className="flip-item">
                <MissedFindingCard finding={f} label={copy.missed} />
              </div>
            ))}
            {!readOnly && (
              <div className="board-add">
                <InputField placeholder={`Add ${col.label}...`} onSubmit={(text) => addItem(rowId, col.id, text)} />
              </div>
            )}
          </div>
        ))}
      </div>

      {isFacilitator && caseActive && !readOnly && (
        <CaseKeyPanel
          tab={tab}
          findings={findings}
          columns={columns}
          withFlag={isLabs}
          placeholders={copy.keyPlaceholder}
          onAddFinding={onAddFinding}
          onRemoveFinding={onRemoveFinding}
        />
      )}
    </div>
  );
}
